import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import eventsData from "../../data/events";
import DownloadDialog from "../../components/Popup/DownloadDialog";
import DownloadIcon from "@mui/icons-material/Download";
import DualMarqueeSlider2 from "../../components/EventSlider";
import mediacovg from "../../assets/images/mediacovg.jpg";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const event = eventsData.find((e) => String(e.id) === String(id));

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");

  const handleDownloadClick = (file, title) => {
    setSelectedFile(file);
    setSelectedTitle(title);
    setOpenDialog(true);
  };

  if (!event)
    return <Box sx={{ p: 10, textAlign: "center" }}>Event not found</Box>;

  // Reusable Section Header Component for consistency
  const SectionHeader = ({ children, highlight }) => (
    <Typography
      variant="h4"
      sx={{
        fontFamily: "Montserrat",
        fontSize: { xs: "1.6rem", md: "2rem" },
        fontWeight: "600",
        mb: 3,
      }}
    >
      {children} <span style={{ color: "#e86c2c" }}>{highlight}</span>
    </Typography>
  );

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* HERO SECTION */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #e86c2c, #d65b1f)",
          color: "white",
          pt: { xs: 16, md: 18 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              cursor: "pointer",
              mb: 4,
              fontSize: "14px",
              fontWeight: 500,
              display: "inline-block",
              opacity: 0.9,
              "&:hover": { opacity: 1 },
            }}
            onClick={() => navigate("/events")}
          >
            ← BACK TO ALL EVENTS
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "32px", md: "56px" },
              lineHeight: 1.1,
              maxWidth: "900px",
              mb: 3,
            }}
          >
            {event?.title}
          </Typography>

          <Typography
            sx={{ fontSize: "1.1rem", opacity: 0.95, display: "flex", gap: 3 }}
          >
            <span>📅 {event?.date}</span>
            <span>📍 {event?.location}</span>
          </Typography>
        </Container>
      </Box>

      {/* MAIN CONTENT & REPORTS SIDEBAR */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6}>
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 6 }}>
              <SectionHeader highlight="Event">About this</SectionHeader>
              {event?.about?.map((para, index) => (
                <Typography
                  key={index}
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.8, fontSize: "1.05rem" }}
                >
                  {para}
                </Typography>
              ))}

              {event?.topics && (
                <Box sx={{ mt: 4 }}>
                  {event.topics.map((topic, index) => (
                    <Typography
                      key={index}
                      color="text.secondary"
                      sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
                    >
                      <Box
                        component="span"
                        sx={{ color: "#e86c2c", mr: 1.5, fontWeight: "bold" }}
                      >
                        •
                      </Box>
                      {topic}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            {/* SLIDER SECTION */}
            <Box
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                bgcolor: "#f9f9f9",
              }}
            >
              <DualMarqueeSlider2 />
            </Box>
          </Grid>

          {/* RIGHT SIDEBAR (REPORTS) */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "sticky", top: "120px" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "700", mb: 3, fontFamily: "Montserrat" }}
              >
                Reports <span style={{ color: "#e86c2c" }}>& Assets</span>
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {event?.reports?.map((report, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #eaeaea",
                      borderRadius: "16px",
                      p: 2.5,
                      transition: "0.3s",
                      "&:hover": {
                        borderColor: "#e86c2c",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Typography
                      fontWeight="600"
                      fontSize="15px"
                      sx={{ mb: 0.5 }}
                    >
                      {report?.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 2 }}
                    >
                      File Size: {report?.size}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        handleDownloadClick(report?.file, report?.title)
                      }
                      sx={{
                        backgroundColor: "#e86c2c",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: "600",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#d65b1f",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Download PDF
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Divider sx={{ maxWidth: "lg", mx: "auto", opacity: 0.6 }} />

      {/* VIDEOS SECTION */}
      {event?.videos?.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <SectionHeader highlight="Details">Study</SectionHeader>
          <Grid container spacing={4}>
            {event.videos.map((video, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
                    mb: 2,
                  }}
                >
                  <iframe
                    src={video.url}
                    title={video.title}
                    frameBorder="0"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                >
                  {video.title}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* MEDIA COVERAGE SECTION */}
      <Box sx={{ bgcolor: "#fcfcfc", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                component="img"
                src={mediacovg}
                alt="Media Coverage"
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "24px",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <SectionHeader highlight="Coverage">Media</SectionHeader>
              <Box
                component="ul"
                sx={{
                  p: 0,
                  m: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {[
                  { name: "MMA News", url: "https://mmaglobal.com/..." },
                  {
                    name: "E4M News",
                    url: "https://www.exchange4media.com/...",
                  },
                  { name: "AFAQS News", url: "https://www.afaqs.com/..." },
                  {
                    name: "LiveMint News",
                    url: "https://www.livemint.com/...",
                  },
                ].map((link, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      borderBottom: "1px solid #eee",
                      pb: 1.5,
                      "& a": {
                        textDecoration: "none",
                        color: "#333",
                        fontSize: "1.1rem",
                        fontWeight: "500",
                        display: "flex",
                        justifyContent: "space-between",
                        transition: "0.2s",
                        "&:hover": { color: "#e86c2c", pl: 1 },
                      },
                    }}
                  >
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.name} <span>↗</span>
                    </a>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* POPUP */}
      <DownloadDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fileUrl={selectedFile}
        title={selectedTitle}
      />
    </Box>
  );
};

export default EventDetails;
